import React from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core";

const Styles = makeStyles((theme: Theme) =>
    createStyles({
        backdrop: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.9)',
            zIndex: 1400, // higher than app bar or modals
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            outline: 'none',
        },
        image: {
            maxHeight: '90vh',
            maxWidth: '90vw',
        },
        closeButton: {
            position: 'absolute',
            top: theme.spacing(2),
            right: theme.spacing(2),
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: '2rem',
            cursor: 'pointer',
        },
        navButton: {
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            color: 'white',
            fontSize: '2rem',
            cursor: 'pointer',
        },
        prevButton: {
            left: theme.spacing(2),
        },
        nextButton: {
            right: theme.spacing(2),
        },
        caption: {
            color: 'white',
            marginTop: theme.spacing(2),
            textAlign: 'center',
            maxWidth: '80vw',
            fontSize: '1rem',
            lineHeight: 1.4,
        },
    })
);

export const AccessibleImageLightBox = (props: {
    images: string[];
    captions?: string[];
    isOpen: boolean;
    photoIndex: number;
    setPhotoIndex: React.Dispatch<React.SetStateAction<number>>;
    handleClose: () => void;
}) => {
    const classes = Styles();
    const lightboxRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (props.isOpen && lightboxRef.current) {
            // Focus the lightbox when it opens
            lightboxRef.current.focus();
            // Prevent body scrolling
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [props.isOpen]);

    if (!props.isOpen) return null;

    return (
        <div
            ref={lightboxRef}
            role="dialog"
            aria-modal="true"
            tabIndex={-1}
            className={classes.backdrop}
            onKeyDown={(e) => {
                if (e.key === 'Escape') props.handleClose();
                if (e.key === 'ArrowRight') {
                    props.setPhotoIndex((props.photoIndex + 1) % props.images.length);
                }
                if (e.key === 'ArrowLeft') {
                    props.setPhotoIndex(
                        (props.photoIndex - 1 + props.images.length) % props.images.length
                    );
                }
            }}
        >
            <button
                onClick={props.handleClose}
                className={classes.closeButton}
                aria-label="Close lightbox"
            >
                ×
            </button>

            <img
                src={props.images[props.photoIndex]}
                alt=""
                className={classes.image}
            />

            {props.captions?.[props.photoIndex] && (
                <div className={classes.caption}>
                    {props.captions[props.photoIndex]}
                </div>
            )}

            {props.images.length > 1 && (
                <>
                    <button
                        onClick={() => {
                            props.setPhotoIndex(
                                (props.photoIndex - 1 + props.images.length) % props.images.length
                            );
                        }}
                        className={`${classes.navButton} ${classes.prevButton}`}
                        aria-label="Previous image"
                    >
                        ‹
                    </button>
                    <button
                        onClick={() => {
                            props.setPhotoIndex((props.photoIndex + 1) % props.images.length);
                        }}
                        className={`${classes.navButton} ${classes.nextButton}`}
                        aria-label="Next image"
                    >
                        ›
                    </button>
                </>
            )}
        </div>
    );
};